/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

/**
 * ChatList with Folders Support
 * 
 * Enhanced ChatList that supports:
 * - Folder organization for chats (folders displayed on TOP)
 * - Create/edit/delete folders
 * - Collapsible folder sections
 * - Chat action popover (delete, move to folder, rename)
 * 
 * Layout order:
 * 1. Folders (collapsible, on top)
 * 2. Favorites
 * 3. All Chats (unfoldered)
 */

import { api } from "@acme/client"
import {
    FaStar,
    toast,
    ConditionalRender,
    AiFillMessage,
    BsFolder2,
    BsFolderPlus,
    BsChevronDown,
    BsChevronRight,
    BsPencil,
    BsTrash,
    BsPlus,
    BsThreeDotsVertical,
    BsFolderSymlink,
} from "@acme/ui"
import { defaultModelPrismaToString, type ChatSession, debounce, type ChatMessage, dayjs } from "@acme/util"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useCallback, useEffect, useMemo, useState, memo, useRef } from "react"
import { chatFavoritesAtom, chatSessionsAtom, chatSessionActiveIdAtom } from "../store/chat"
import { teamActiveAtom } from "../store/user"
import { useRouter } from "next/router"
import { selectAtom } from "jotai/utils"
import { chatFoldersAtom, chatFolderMappingAtom, type ChatFolder } from "../store/setting"
import { nanoid } from "nanoid"
import { motion, AnimatePresence } from "framer-motion"


// =============================================================================
// UTILS
// =============================================================================

const intersectById = (localStorageData: ChatSession[], apiData: ChatSession[]): ChatSession[] => {
    const localStorageDataMap = new Map(localStorageData.map(item => [item.id, item]))

    return apiData.map(apiItem => {
        const localStorageItem = localStorageDataMap.get(apiItem.id)

        if (localStorageItem) {
            localStorageDataMap.delete(apiItem.id)
            return {
                ...localStorageItem,
                ...apiItem,
                messages: [...localStorageItem.messages],
                isInit: false,
            }
        }

        return apiItem
    }).concat(Array.from(localStorageDataMap.values()))
}

function sortChatSessions(chatSessions: ChatSession[]): ChatSession[] {
    return chatSessions.sort((a, b) => {
        const aOrder = a.order || 100;
        const bOrder = b.order || 100;

        if (aOrder && bOrder) {
            return aOrder - bOrder
        } else if (aOrder === bOrder) {
            const aOrderUpdated = a.orderUpdated ? a.orderUpdated : a.created;
            const bOrderUpdated = b.orderUpdated ? b.orderUpdated : b.created;
            if (aOrderUpdated && b.orderUpdated) {
                return dayjs(aOrderUpdated).valueOf() - dayjs(bOrderUpdated).valueOf();
            } else if (aOrderUpdated && !bOrderUpdated) {
                return -1;
            } else if (bOrderUpdated && !aOrderUpdated) {
                return 1;
            } else {
                return 0;
            }
        } else if (aOrder && !bOrder) {
            return aOrder
        } else if (!aOrder && bOrder) {
            return bOrder
        }

        return 0
    });
}

function sortChatSessionsByLastMessage(chatSessions: ChatSession[]): ChatSession[] {
    return chatSessions.sort((a, b) => {
        const aLastMessage = a.messages[a.messages.length - 1];
        const bLastMessage = b.messages[b.messages.length - 1];
        const aDate = aLastMessage ? dayjs(aLastMessage.created) : dayjs(a.created);
        const bDate = bLastMessage ? dayjs(bLastMessage.created) : dayjs(b.created);
        return bDate.diff(aDate);
    });
}

export const chatSessionsNoMessage = selectAtom(chatSessionsAtom, (chatSessions) => chatSessions.map(n => ({
    ...n,
    messages: (() => { const kept = (n?.messages || []).filter(m => !m.isCompare); return kept.length ? [kept[kept.length - 1] as ChatMessage] : [] })() // last kept message; compare replies aren't the chat's answer
}) satisfies ChatSession))


// =============================================================================
// FOLDER COLORS
// =============================================================================

const getColorClasses = (color?: string): { icon: string; bg: string; border: string } => {
    const defaultColor = { icon: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/20' };
    const colors: Record<string, { icon: string; bg: string; border: string }> = {
        gray: defaultColor,
        red: { icon: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20' },
        orange: { icon: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
        amber: { icon: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
        green: { icon: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/20' },
        blue: { icon: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
        purple: { icon: 'text-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
        pink: { icon: 'text-pink-500', bg: 'bg-pink-500/10', border: 'border-pink-500/20' },
    };
    return colors[color || 'gray'] ?? defaultColor;
};


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ChatListWithFolders = () => {
    const router = useRouter()
    const chatId = router.query.chatId as string

    const teamActive = useAtomValue(teamActiveAtom)
    const chatSessions = useAtomValue(chatSessionsNoMessage)
    const [chatSessionActiveId, setChatSessionActiveId] = useAtom(chatSessionActiveIdAtom)
    const chatFavorites = useAtomValue(chatFavoritesAtom)
    const setChatSessions = useSetAtom(chatSessionsAtom)
    const setChatFavorites = useSetAtom(chatFavoritesAtom)
    const [folders, setFolders] = useAtom(chatFoldersAtom) as [ChatFolder[], (update: ChatFolder[] | ((prev: ChatFolder[]) => ChatFolder[])) => void]
    const [folderMapping, setFolderMapping] = useAtom(chatFolderMappingAtom) as [Record<string, string>, (update: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void]
    const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set())
    const [isCreatingFolder, setIsCreatingFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')

    // API queries - same as original ChatList
    api.chat.chatFavorites.useQuery(undefined,
        {
            enabled: !!teamActive?.id,
            onSuccess: (data) => {
                const cloudFavorites = data.map(f => ({ chatSessionId: f.chatSessionId, created: f.created as Date, updated: f.updated as Date }))
                setChatFavorites((favorites) => {
                    const localFavorites = favorites.filter(s => !cloudFavorites.some(a => a.chatSessionId === s.chatSessionId));
                    const updatedCloudFavorites = cloudFavorites.map(cloudFavorite => {
                        const localFavorite = favorites.find(fav => fav.chatSessionId === cloudFavorite.chatSessionId);
                        return { ...localFavorite, ...cloudFavorite, isSync: true };
                    });
                    return [...updatedCloudFavorites, ...localFavorites];
                });
            },
            staleTime: 20000
        }
    )

    const { mutateAsync: onUpdateChatSession } = api.chat.updateChatSession.useMutation()

    api.chat.chatSessions.useQuery(
        { teamId: teamActive?.id },
        {
            enabled: !!teamActive?.id,
            onSuccess: (data) => {
                const newChatSessions: ChatSession[] = (data || []).map(s => {
                    return {
                        ...s,
                        localId: s.localId || '',
                        name: s.name || '',
                        isStreaming: s.isStreaming ? s.isStreaming : false,
                        defaultInstruction: s.defaultInstruction || undefined,
                        messages: [],
                        defaultModel: s.defaultModel ? defaultModelPrismaToString(s.defaultModel) : undefined,
                        created: s.created as Date,
                        updated: s.updated as Date,
                        isImageGen: s.isImageGen ? s.isImageGen : false,
                        order: s.order || undefined,
                        orderUpdated: s.orderUpdated || undefined,
                        isInit: false,
                        isSync: true
                    }
                })
                setChatSessions(chatSessions => {
                    const chatSessionsFiltered = chatSessions.filter(s => {
                        if (s.isSync && !s.isInit) {
                            return newChatSessions.some(a => a.id === s.id)
                        }
                        return true
                    })
                    const newChatSessionsIntersect = intersectById(chatSessionsFiltered, newChatSessions)
                    const newChatSessionsSorted = sortChatSessions(newChatSessionsIntersect)
                    return newChatSessionsSorted
                })
            },
            staleTime: 5000
        },
    )

    const { mutateAsync: onDeleteChatSession } = api.chat.deleteChatSession.useMutation({
        onSuccess: (data) => {
            setChatSessions(prev => prev.filter(a => a.id !== data.id))
        }
    })

    // Memoized data
    const chatSessionsSorted = useMemo(() => sortChatSessionsByLastMessage(chatSessions), [chatSessions])

    const chatSessionsFavorites = useMemo(() => {
        return chatSessionsSorted
            .filter(s => chatFavorites.some(f => f.chatSessionId === s.id))
            .map(s => {
                const lastMessage = s.messages[s.messages.length - 1]?.content?.content || s.messages[s.messages.length - 1]?.content?.content?.[0] || ''
                return { ...s, lastMessage }
            })
    }, [chatSessionsSorted, chatFavorites])

    // Get chats in a folder using the mapping
    const getChatsInFolder = useCallback((folderId: string): ChatSession[] => {
        return chatSessionsSorted.filter(chat =>
            folderMapping[chat.id] === folderId &&
            !chatFavorites.some(f => f.chatSessionId === chat.id)
        );
    }, [chatSessionsSorted, folderMapping, chatFavorites]);

    // Chats not in any folder and not favorites
    const unfolderedChats = useMemo(() => {
        return chatSessionsSorted.filter(s =>
            !chatFavorites.some(f => f.chatSessionId === s.id) &&
            !folderMapping[s.id]
        );
    }, [chatSessionsSorted, chatFavorites, folderMapping]);

    // Callbacks
    const onDeleteSession = useCallback(async (id: string) => {
        if (chatSessions.length === 1) {
            toast.error('Cannot delete last session')
            return
        }
        const chatSession = chatSessions.find(a => a.id === id)
        if (!chatSession) return

        // Remove from folder mapping
        setFolderMapping(prev => {
            const newMapping = { ...prev };
            delete newMapping[id];
            return newMapping;
        });

        if (chatSession.isSync && !chatSession.isInit) {
            setChatSessions(prev => prev.map(a => {
                if (a.id === id) return { ...a, loading: true }
                return a
            }))
            try {
                await onDeleteChatSession({ id })
            } catch (error) {
                if (error instanceof Error) {
                    if (error.message.includes('UNAUTHORIZED')) {
                        setChatSessions(prev => prev.filter(a => a.id !== id))
                        if (chatSessionActiveId === id) {
                            const newId = chatSessions.find(a => a.id !== id)?.id || ''
                            setChatSessionActiveId(newId)
                            await router.push({ query: { ...router.query, chatId: newId } })
                        }
                        toast.error("Chat session deleted locally")
                        return
                    }
                }
                toast.error("Error deleting chat session")
                setChatSessions(prev => prev.map(a => {
                    if (a.id === id) return { ...a, loading: undefined }
                    return a
                }))
            }
        } else {
            setChatSessions(prev => prev.filter(a => a.id !== id))
        }

        if (chatSessionActiveId === id) {
            const newId = chatSessions.find(a => a.id !== id)?.id || ''
            setChatSessionActiveId(newId)
            await router.push({ query: { ...router.query, chatId: newId } })
        }
    }, [chatSessions, chatSessionActiveId, setChatSessions, onDeleteChatSession, setChatSessionActiveId, router, setFolderMapping])

    const onEdit = useCallback(async (id: string, name: string) => {
        const findSession = chatSessions.find(a => a.id === id)
        if (!findSession) return
        if (findSession.isSync && !findSession.isInit) {
            await onUpdateChatSession({ id, name })
        }
        setChatSessions(prev => prev.map(a => {
            if (a.id === id) return { ...a, name }
            return a
        }))
    }, [chatSessions, onUpdateChatSession, setChatSessions])

    const onSelect = useCallback(async (id: string) => {
        await router.push({ query: { ...router.query, chatId: id } })
    }, [router])

    const onSetChatSessionActiveDebounce = useMemo(() => debounce((id: string) => setChatSessionActiveId(id), 70), [setChatSessionActiveId])

    useEffect(() => {
        if (chatId && router?.isReady) {
            if (chatId !== chatSessionActiveId) {
                onSetChatSessionActiveDebounce(chatId)
            }
        }
    }, [chatId, chatSessionActiveId, onSetChatSessionActiveDebounce, router?.isReady])

    // Folder management
    const toggleFolderCollapse = useCallback((folderId: string) => {
        setCollapsedFolders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(folderId)) {
                newSet.delete(folderId);
            } else {
                newSet.add(folderId);
            }
            return newSet;
        });
    }, []);

    const createFolder = useCallback(() => {
        if (!newFolderName.trim()) return;
        const now = new Date();
        const newFolder: ChatFolder = {
            id: nanoid(),
            name: newFolderName.trim(),
            color: 'gray',
            order: folders.length,
            created: now,
            updated: now,
        };
        setFolders(prev => [...prev, newFolder]);
        setNewFolderName('');
        setIsCreatingFolder(false);
        toast.success('Folder created');
    }, [newFolderName, setFolders, folders.length]);

    const deleteFolder = useCallback((folderId: string) => {
        // Remove folder
        setFolders(prev => prev.filter(f => f.id !== folderId));
        // Remove all mappings for this folder
        setFolderMapping(prev => {
            const newMapping: Record<string, string> = {};
            Object.entries(prev).forEach(([chatId, mappedFolderId]) => {
                if (mappedFolderId !== folderId) {
                    newMapping[chatId] = mappedFolderId;
                }
            });
            return newMapping;
        });
        toast.success('Folder deleted');
    }, [setFolders, setFolderMapping]);

    const renameFolder = useCallback((folderId: string, newName: string) => {
        setFolders(prev => prev.map(f =>
            f.id === folderId ? { ...f, name: newName, updated: new Date() } : f
        ));
    }, [setFolders]);

    const addChatToFolder = useCallback((chatId: string, folderId: string) => {
        setFolderMapping(prev => ({
            ...prev,
            [chatId]: folderId
        }));
    }, [setFolderMapping]);

    const removeChatFromFolder = useCallback((chatId: string) => {
        setFolderMapping(prev => {
            const newMapping = { ...prev };
            delete newMapping[chatId];
            return newMapping;
        });
    }, [setFolderMapping]);

    // Sort folders by order
    const sortedFolders = useMemo(() =>
        [...folders].sort((a, b) => a.order - b.order),
        [folders]
    );

    return (
        <>
            <div className="border-b-[1px] border-neutral-700/50 ml-2" />
            <div className="flex flex-col gap-2">
                {/* 1. FOLDERS SECTION (ON TOP) */}
                <div className="flex flex-col gap-0.5">
                    <div className="flex flex-row items-center justify-between px-3 pt-2 pb-1">
                        <div className="flex items-center gap-1.5">
                            <BsFolder2 className="text-gray-500 text-xs" />
                            <p className="text-gray-500 text-[10px] uppercase font-medium tracking-wide">Folders</p>
                        </div>
                        <button
                            onClick={() => setIsCreatingFolder(true)}
                            className="p-0.5 hover:bg-neutral-700 rounded transition-colors"
                            title="Create folder"
                        >
                            <BsFolderPlus className="text-gray-500 text-xs hover:text-white" />
                        </button>
                    </div>

                    {/* Create Folder Input */}
                    <AnimatePresence>
                        {isCreatingFolder && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="px-2 pb-1"
                            >
                                <div className="flex items-center gap-1.5">
                                    <input
                                        type="text"
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') createFolder();
                                            if (e.key === 'Escape') setIsCreatingFolder(false);
                                        }}
                                        placeholder="Folder name..."
                                        className="flex-1 px-2 py-1 text-xs bg-neutral-800 border border-neutral-700 rounded focus:outline-none focus:border-blue-500 text-white"
                                        autoFocus
                                    />
                                    <button
                                        onClick={createFolder}
                                        className="p-1 bg-blue-600 hover:bg-blue-700 rounded text-white"
                                    >
                                        <BsPlus className="text-sm" />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Folder List */}
                    <div className="px-1">
                        {sortedFolders.map(folder => (
                            <FolderSection
                                key={folder.id}
                                folder={folder}
                                chats={getChatsInFolder(folder.id)}
                                chatSessionActiveId={chatSessionActiveId ?? ''}
                                isCollapsed={collapsedFolders.has(folder.id)}
                                onToggleCollapse={() => toggleFolderCollapse(folder.id)}
                                onEdit={onEdit}
                                onDelete={onDeleteSession}
                                onSelect={onSelect}
                                onRenameFolder={renameFolder}
                                onDeleteFolder={deleteFolder}
                                onRemoveFromFolder={removeChatFromFolder}
                                allFolders={sortedFolders}
                                onAddToFolder={addChatToFolder}
                            />
                        ))}
                    </div>

                    {/* {sortedFolders.length === 0 && !isCreatingFolder && (
                        <p className="text-[10px] text-gray-600 px-3 py-1">No folders</p>
                    )} */}
                </div>

                {/* 2. FAVORITES SECTION */}
                <ConditionalRender show={!!chatSessionsFavorites.length}>
                    <div className="flex flex-col pl-2 gap-0.5">
                        <div className="flex flex-row items-center gap-1.5 px-1 pt-1 pb-1">
                            <FaStar className="text-yellow-500 text-xs" />
                            <p className="text-yellow-500 text-[10px] uppercase font-medium tracking-wide">Favorites</p>
                        </div>
                        {chatSessionsFavorites.map(a => (
                            <ChatSessionBubble
                                key={a.id}
                                isActive={a.id === chatSessionActiveId}
                                chatSession={a}
                                onEdit={onEdit}
                                onDelete={onDeleteSession}
                                onSelect={onSelect}
                                isFavorite
                                folders={sortedFolders}
                                onAddToFolder={addChatToFolder}
                            />
                        ))}
                    </div>
                </ConditionalRender>

                {/* 3. ALL CHATS (Unfoldered) */}
                <div className="flex flex-col pl-2 gap-0.5">
                    <div className="flex flex-row items-center gap-1.5 px-1 pt-1 pb-1">
                        <AiFillMessage className="text-gray-500 text-xs" />
                        <p className="text-gray-500 text-[10px] uppercase font-medium tracking-wide">All Chats</p>
                    </div>
                    {unfolderedChats.map(a => (
                        <ChatSessionBubble
                            key={a.id}
                            isActive={a.id === chatSessionActiveId}
                            chatSession={a}
                            onEdit={onEdit}
                            onDelete={onDeleteSession}
                            onSelect={onSelect}
                            folders={sortedFolders}
                            onAddToFolder={addChatToFolder}
                        />
                    ))}
                </div>
            </div>
        </>
    )
}


// =============================================================================
// FOLDER SECTION COMPONENT
// =============================================================================

type FolderSectionProps = {
    folder: ChatFolder;
    chats: ChatSession[];
    chatSessionActiveId: string;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    onEdit: (id: string, name: string) => void;
    onDelete: (id: string) => void;
    onSelect: (id: string) => void;
    onRenameFolder: (folderId: string, newName: string) => void;
    onDeleteFolder: (folderId: string) => void;
    onRemoveFromFolder: (chatId: string) => void;
    allFolders: ChatFolder[];
    onAddToFolder: (chatId: string, folderId: string) => void;
};

const FolderSection = memo<FolderSectionProps>(({
    folder,
    chats,
    chatSessionActiveId,
    isCollapsed,
    onToggleCollapse,
    onEdit,
    onDelete,
    onSelect,
    onRenameFolder,
    onDeleteFolder,
    onRemoveFromFolder,
    allFolders,
    onAddToFolder,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(folder.name);
    const colors = getColorClasses(folder.color);

    const handleRename = () => {
        if (editName.trim() && editName.trim() !== folder.name) {
            onRenameFolder(folder.id, editName.trim());
        }
        setIsEditing(false);
    };

    return (
        <div className="mb-0.5">
            {/* Folder Header - Clean minimal design */}
            <div
                className="flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer group hover:bg-neutral-800/60 transition-colors"
                onClick={onToggleCollapse}
            >
                {/* Expand/Collapse Arrow */}
                <div className="w-4 flex items-center justify-center flex-shrink-0">
                    {isCollapsed ? (
                        <BsChevronRight className="text-gray-500 text-[10px]" />
                    ) : (
                        <BsChevronDown className="text-gray-500 text-[10px]" />
                    )}
                </div>

                {/* Folder Icon */}
                <BsFolder2 className={`text-xs flex-shrink-0 ${colors.icon}`} />

                {/* Folder Name */}
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    {isEditing ? (
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={handleRename}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRename();
                                if (e.key === 'Escape') {
                                    setEditName(folder.name);
                                    setIsEditing(false);
                                }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 px-1 py-0.5 text-xs bg-neutral-700 border border-neutral-600 rounded text-white focus:outline-none focus:border-blue-500 min-w-0"
                            autoFocus
                        />
                    ) : (
                        <span className="text-xs text-gray-300 truncate font-medium">{folder.name}</span>
                    )}
                    <span className="text-[10px] text-gray-500">({chats.length})</span>
                </div>

                {/* Folder Actions - Show on hover */}
                <div className="hidden group-hover:flex items-center" onClick={(e) => e.stopPropagation()}>
                    <button
                        onClick={() => {
                            setEditName(folder.name);
                            setIsEditing(true);
                        }}
                        className="p-0.5 hover:bg-neutral-600 rounded"
                        title="Rename"
                    >
                        <BsPencil className="text-gray-400 text-[10px]" />
                    </button>
                    <button
                        onClick={() => onDeleteFolder(folder.id)}
                        className="p-0.5 hover:bg-red-900/50 rounded"
                        title="Delete"
                    >
                        <BsTrash className="text-gray-400 hover:text-red-400 text-[10px]" />
                    </button>
                </div>
            </div>

            {/* Folder Contents */}
            <AnimatePresence>
                {!isCollapsed && chats.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="ml-4 mt-1"
                    >
                        {chats.map(chat => (
                            <ChatSessionBubble
                                key={chat.id}
                                isActive={chat.id === chatSessionActiveId}
                                chatSession={chat}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onSelect={onSelect}
                                inFolder={folder.id}
                                onRemoveFromFolder={() => onRemoveFromFolder(chat.id)}
                                folders={allFolders}
                                onAddToFolder={onAddToFolder}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
});

FolderSection.displayName = 'FolderSection';


// =============================================================================
// CHAT SESSION BUBBLE WITH ACTIONS POPOVER
// =============================================================================

type ChatSessionBubbleProps = {
    chatSession: ChatSession
    isActive: boolean
    isFavorite?: boolean
    inFolder?: string
    onEdit: (id: string, name: string) => void
    onDelete: (id: string) => void
    onSelect: (id: string) => void
    onRemoveFromFolder?: () => void
    folders?: ChatFolder[]
    onAddToFolder?: (chatId: string, folderId: string) => void
}

export const ChatSessionBubble = memo<ChatSessionBubbleProps>(({
    chatSession,
    isFavorite: _isFavorite,
    onDelete,
    onSelect,
    onEdit,
    isActive = false,
    inFolder,
    onRemoveFromFolder,
    folders = [],
    onAddToFolder,
}) => {
    const { name, messages, loading = false } = chatSession
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [isFolderSubmenuOpen, setIsFolderSubmenuOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editName, setEditName] = useState(name)
    const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)
    const popoverRef = useRef<HTMLDivElement>(null)

    const lastMessage = useMemo(() => {
        if (messages.length === 0) return ''
        const veryLast = messages[messages.length - 1]?.content?.content || messages[messages.length - 1]?.content?.content?.[0] || ''
        if (!veryLast && messages.length > 1) {
            const last2 = messages[messages.length - 2]?.content?.content || messages[messages.length - 2]?.content?.content?.[0] || ''
            if (Array.isArray(last2)) return last2.join(' ')
            return last2
        }
        if (Array.isArray(veryLast)) return veryLast.join(' ')
        return veryLast
    }, [messages])

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setIsPopoverOpen(false)
                setIsFolderSubmenuOpen(false)
            }
        }
        if (isPopoverOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isPopoverOpen])

    const handleRename = () => {
        if (editName.trim() && editName.trim() !== name) {
            onEdit(chatSession.id, editName.trim())
        }
        setIsEditing(false)
    }

    const handleMoveToFolder = (folderId: string) => {
        if (onAddToFolder) {
            onAddToFolder(chatSession.id, folderId)
            toast.success('Moved to folder')
        }
        setIsPopoverOpen(false)
        setIsFolderSubmenuOpen(false)
    }

    const bgClass = isActive ? 'bg-neutral-700' : 'bg-transparent'

    return (
        <div className="relative group">
            <div
                role="button"
                tabIndex={0}
                className={`w-full border-b border-neutral-600 border-opacity-30 bg-transparent !duration-100 pb-[9px] pt-[6px] !pr-2 rounded cursor-pointer ${isActive ? 'bg-neutral-700' : 'hover:bg-neutral-800'} ${bgClass} ${isActive ? 'hover:opacity-100' : 'hover:opacity-80'} transition-all duration-150 text-white px-2`}
                onClick={() => onSelect(chatSession.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(chatSession.id) }}
            >
                <div className="flex flex-row items-center justify-start gap-2 w-full relative">
                    {loading && (
                        <div className="w-[100%] h-[30px] gap-2 absolute bg-neutral-800 px-2 z-50 rounded-full flex flex-row items-center border-opacity-50">
                            <div className="animate-spin text-xl">⏳</div>
                            <p>Loading...</p>
                        </div>
                    )}

                    {chatSession.isSync && (
                        <div className="absolute left-[-13px] top-[-8px] text-2xl z-50 text-green-500">•</div>
                    )}

                    {isDeleteConfirm ? (
                        <div className={`w-full h-full z-10 flex flex-row items-center gap-2 ${bgClass}`}>
                            <p className="text-sm text-red-300 flex-1 text-left">Confirm Delete?</p>
                            <div className="flex flex-row gap-2">
                                <p
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsDeleteConfirm(false)
                                    }}
                                    className="text-sm p-1 rounded bg-gray-500 px-2 hover:opacity-70 cursor-pointer"
                                >
                                    No
                                </p>
                                <p
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onDelete(chatSession.id)
                                    }}
                                    className="text-sm p-1 rounded bg-red-500 px-2 hover:opacity-70 cursor-pointer"
                                >
                                    Yes
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-row items-center justify-between relative space-x-4 max-w-full w-full">
                            <div className="flex flex-col gap-[1px] flex-1 flex-grow items-start relative min-w-0">
                                {isEditing ? (
                                    <input
                                        value={editName}
                                        className="bg-transparent border-b-[1px] border-b-white border rounded px-1 outline-none text-sm w-full"
                                        onChange={(e) => setEditName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.stopPropagation()
                                                handleRename()
                                            }
                                            if (e.key === 'Escape') {
                                                setEditName(name)
                                                setIsEditing(false)
                                            }
                                        }}
                                        onBlur={handleRename}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                ) : (
                                    <p className="text-sm font-semibold text-left">{name}</p>
                                )}
                                {lastMessage && (
                                    <p className="text-[11px] pt-[0px] text-gray-400 h-[19px] font-light text-left truncate flex-grow max-w-full min-w-full">
                                        {lastMessage || 'ㅤ'}
                                    </p>
                                )}
                            </div>

                            {/* Action Button - Three dots */}
                            <div
                                className={`flex-shrink-0 ${(!isActive && !isPopoverOpen) ? 'invisible opacity-0' : ''} group-hover:visible group-hover:opacity-100`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsPopoverOpen(!isPopoverOpen)
                                    }}
                                    className="p-1 hover:bg-neutral-600 rounded transition-colors"
                                >
                                    <BsThreeDotsVertical className="text-gray-400 hover:text-white text-sm" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions Popover */}
            <AnimatePresence>
                {isPopoverOpen && (
                    <motion.div
                        ref={popoverRef}
                        initial={{ opacity: 0, scale: 0.95, y: -5 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -5 }}
                        transition={{ duration: 0.1 }}
                        className="absolute right-0 top-full mt-1 z-50 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl min-w-[160px] py-1"
                    >
                        {/* Rename */}
                        <button
                            onClick={() => {
                                setEditName(name)
                                setIsEditing(true)
                                setIsPopoverOpen(false)
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-neutral-700 flex items-center gap-2 transition-colors"
                        >
                            <BsPencil className="text-gray-400" />
                            Rename
                        </button>

                        {/* Move to Folder - Opens submenu below */}
                        <div className="relative">
                            <button
                                onClick={() => setIsFolderSubmenuOpen(!isFolderSubmenuOpen)}
                                className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-neutral-700 flex items-center gap-2 justify-between transition-colors"
                            >
                                <span className="flex items-center gap-2">
                                    <BsFolderSymlink className="text-gray-400" />
                                    Move to Folder
                                </span>
                                <BsChevronDown className={`text-gray-500 text-xs transition-transform ${isFolderSubmenuOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Folder Submenu - Opens below */}
                            <AnimatePresence>
                                {isFolderSubmenuOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="bg-neutral-750 border-t border-neutral-700 max-h-[150px] overflow-y-auto"
                                    >
                                        {inFolder && onRemoveFromFolder && (
                                            <button
                                                onClick={() => {
                                                    onRemoveFromFolder()
                                                    setIsPopoverOpen(false)
                                                    setIsFolderSubmenuOpen(false)
                                                    toast.success('Removed from folder')
                                                }}
                                                className="w-full px-4 py-1.5 text-left text-xs text-orange-400 hover:bg-neutral-700 flex items-center gap-2"
                                            >
                                                <BsFolder2 className="text-orange-400 text-xs" />
                                                Remove from folder
                                            </button>
                                        )}
                                        {folders.length === 0 ? (
                                            <p className="px-4 py-2 text-xs text-gray-500">No folders yet</p>
                                        ) : (
                                            folders
                                                .filter(f => f.id !== inFolder)
                                                .map(folder => (
                                                    <button
                                                        key={folder.id}
                                                        onClick={() => handleMoveToFolder(folder.id)}
                                                        className="w-full px-4 py-1.5 text-left text-xs text-gray-300 hover:bg-neutral-700 flex items-center gap-2"
                                                    >
                                                        <BsFolder2 className={`text-xs ${getColorClasses(folder.color).icon}`} />
                                                        <span className="truncate">{folder.name}</span>
                                                    </button>
                                                ))
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="border-t border-neutral-700 my-1" />

                        {/* Delete */}
                        <button
                            onClick={() => {
                                setIsDeleteConfirm(true)
                                setIsPopoverOpen(false)
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-red-900/30 flex items-center gap-2 transition-colors"
                        >
                            <BsTrash className="text-red-400" />
                            Delete
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
});

ChatSessionBubble.displayName = 'ChatSessionBubble';

export default ChatListWithFolders;
